/**
 * Light / dark theming.
 *
 * Two ideas kept apart: the PREFERENCE, which is "system" until someone chooses
 * otherwise, and the RESOLVED theme actually applied. Collapsing them would break
 * following the system — a preference flattened to whatever the OS said at first
 * load stops tracking it when the OS changes at sunset.
 *
 * The class hook is `.dark` on <html>, matching the `@custom-variant dark` already
 * declared in index.css, so every existing `dark:` utility works unchanged.
 */

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_KEY = "mavenTheme";

export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (preference === "system") return systemPrefersDark ? "dark" : "light";
  return preference;
}

/**
 * What the toggle should switch to. From "system" it picks the opposite of what is
 * currently on screen — otherwise the first click would appear to do nothing.
 */
export function nextTheme(preference: ThemePreference, systemPrefersDark: boolean): ThemePreference {
  return resolveTheme(preference, systemPrefersDark) === "dark" ? "light" : "dark";
}

function isPreference(value: unknown): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

/** Falls back to "system" for anything missing, unrecognised, or unreadable. */
export function readStoredTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return isPreference(stored) ? stored : "system";
  } catch {
    // Private windows and blocked site data throw rather than returning null.
    return "system";
  }
}

export function storeTheme(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_KEY, preference);
  } catch {
    // A preference that cannot be saved is still worth applying for this session.
  }
}

export function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  // Tells the browser to render form controls, scrollbars and the like to match —
  // without it, native widgets stay light on a dark page.
  root.style.colorScheme = theme;
}
